import React, { useState, useEffect } from "react";
import api from "../../../../utils/apiConfig";
import MemberCard from "./career-education/MemberCard";
import EmploymentModal from "./career-education/EmploymentModal";
import EducationModal from "./career-education/EducationModal";
import MilitaryModal from "./career-education/MilitaryModal";
import TimelineView from "./career-education/TimelineView";
import { formatMilitaryBranch } from "../../../../utils/militaryUtils";
import { useAuth } from "../../../../contexts/AuthContext";
import { useParams, useLocation } from "react-router-dom";

import logger from '../../../../utils/logger';

const CareerEducation = () => {
  const { householdId } = useParams();
  const location = useLocation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'timeline'
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalType, setModalType] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) {
          throw new Error("No token found");
        }
        if (!householdId) {
          throw new Error("Household ID is missing");
        }

        // Get the current client's ID from the URL if we're on a client-specific page
        const currentClientId = location.pathname.includes("/client-profile/")
          ? parseInt(
              location.pathname.split("/client-profile/")[1].split("/")[0],
            )
          : null;

        // Use client-specific endpoint if we're on a client profile page
        const endpoint = currentClientId
          ? `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${currentClientId}/career-education`
          : `/advisor-portal/client-database/household-profile/${householdId}/career-education`;

        logger.info(
          `📋 Fetching career education data for ${currentClientId ? `client: ${currentClientId}` : `household: ${householdId}`}`,
        );

        const response = await api.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        logger.info("✅ Raw API response:", {
          status: response.status,
          data: response.data,
          membersExists: !!response.data?.members,
          memberCount: response.data?.members?.length || 0,
        });

        if (!response.data || !response.data.members) {
          logger.error("❌ Invalid response format:", {
            hasData: !!response.data,
            dataKeys: response.data ? Object.keys(response.data) : [],
            membersExists: !!response.data?.members,
          });
          throw new Error("Invalid response format: missing members data");
        }

        // Sort members to prioritize the current client if we're on a client-specific page
        const sortedMembers = [...response.data.members].sort((a, b) => {
          if (a.isCurrentClient) return -1;
          if (b.isCurrentClient) return 1;
          return 0;
        });

        logger.info(
          "🔄 Sorted members:",
          sortedMembers.map((member) => ({
            userId: member.userId,
            name: `${member.firstName || ""} ${member.lastName || ""}`.trim(),
            isCurrentClient: member.isCurrentClient,
          })),
        );

        // Validate and transform member data
        const validatedMembers = sortedMembers
          .map((member, index) => {
            if (!member) {
              logger.warn(`Member at index ${index} is null or undefined`);
              return null;
            }

            // Log detailed member data for debugging
            logger.info(`👤 Member ${index + 1} details:`, {
              id: member.userId,
              name:
                `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
                "Unknown",
              isCurrentClient: member.isCurrentClient,
              hasEmployment:
                Array.isArray(member.employment) &&
                member.employment.length > 0,
              employmentCount: Array.isArray(member.employment)
                ? member.employment.length
                : 0,
              hasEducation:
                Array.isArray(member.education) && member.education.length > 0,
              educationCount: Array.isArray(member.education)
                ? member.education.length
                : 0,
              hasMilitary: !!member.military,
              rawMember: member,
            });

            const transformedMember = {
              ...member,
              employment: Array.isArray(member.employment)
                ? member.employment
                : [],
              education: Array.isArray(member.education)
                ? member.education
                : [],
              military: member.military || null,
            };

            logger.info(
              `✨ Transformed member ${index + 1}:`,
              transformedMember,
            );
            return transformedMember;
          })
          .filter(Boolean); // Remove any null entries

        logger.info("✅ Final validated members:", validatedMembers);
        setMembers(validatedMembers);
      } catch (err) {
        logger.error("❌ Error fetching career education data:", err);
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to load data";
        logger.error("Error details:", {
          status: err.response?.status,
          message: errorMessage,
          url: err.config?.url,
        });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [householdId, token, location.pathname]);

  const handleEdit = (member, type, record = null) => {
    // If a specific record is passed (from table view), use that
    // Otherwise, check if we're editing an existing record
    let recordToEdit = null;

    if (record) {
      // Coming from table view edit button
      recordToEdit = record;
    } else {
      // Coming from "Add" button or other sources
      if (type === "employment") {
        recordToEdit = member.employment?.[0] || null;
      } else if (type === "education") {
        recordToEdit = member.education?.[0] || null;
      } else if (type === "military") {
        recordToEdit = member.military;
      }
    }

    setSelectedMember({
      ...member,
      [type]: recordToEdit,
    });
    setModalType(type);
  };

  const renderTableView = (member, index) => (
    <div className="mt-4 overflow-x-auto">
      <div className="mb-2">
        {(!member.employment || member.employment.length === 0) &&
          (!member.education || member.education.length === 0) &&
          !member.military && (
            <div className="text-center py-3 text-gray-500 dark:text-gray-500 italic">
              No career or education history found
            </div>
          )}
      </div>

      {(member.employment?.length > 0 ||
        member.education?.length > 0 ||
        member.military) && (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Title/Institution
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Start Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                End Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
            {/* Employment Records */}
            {member.employment?.map((record) => (
              <tr key={`employment-${record.id}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                    Employment
                  </span>
                </td>
                <td className="px-6 py-4">{record.employerName}</td>
                <td className="px-6 py-4">{record.startDate}</td>
                <td className="px-6 py-4">{record.endDate || "Present"}</td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      record.employmentStatus === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {record.employmentStatus}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleEdit(member, "employment", record)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}

            {/* Education Records */}
            {member.education?.map((record) => (
              <tr key={`education-${record.id}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                    Education
                  </span>
                </td>
                <td className="px-6 py-4">{record.institutionName}</td>
                <td className="px-6 py-4">{record.startDate}</td>
                <td className="px-6 py-4">
                  {record.graduationDate || "In Progress"}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      record.currentlyEnrolled
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {record.currentlyEnrolled ? "Enrolled" : "Completed"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleEdit(member, "education", record)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}

            {/* Military Record */}
            {member.military && (
              <tr key={`military-${member.id}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    Military
                  </span>
                </td>
                <td className="px-6 py-4">
                  {formatMilitaryBranch(member.military.branchOfService)}
                </td>
                <td className="px-6 py-4">{member.military.startDate}</td>
                <td className="px-6 py-4">
                  {member.military.endDate || "Present"}
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:text-gray-200 rounded-full">
                    {member.military.typeOfService}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() =>
                      handleEdit(member, "military", member.military)
                    }
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4">
        <p className="font-medium">Error loading career & education data</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  const transformedMembers = members.map((member, index) => {
    // Log each member's data for debugging
    logger.info(`Member ${index} data:`, {
      userId: member.userId,
      firstName: member.firstName,
      lastName: member.lastName,
      hasName: Boolean(member.firstName || member.lastName),
      isCurrentClient: member.isCurrentClient,
      employmentCount: member.employment?.length || 0,
      educationCount: member.education?.length || 0,
      hasMilitary: !!member.military,
    });

    return {
      ...member,
      key: member.userId || `member-${index}`,
      employment: member.employment || [],
      education: member.education || [],
      military: member.military || null,
      displayName:
        `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
        "Household Member",
    };
  });

  logger.info("Transformed members:", transformedMembers);

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow max-w-7xl mx-auto">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Career & Education Information
      </h2>

      <div className="mb-6 flex justify-center">
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-1">
          <button
            onClick={() => setViewMode("table")}
            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center ${
              viewMode === "table"
                ? "bg-blue-600 text-white"
                : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M3 14h18M3 18h18M3 6h18"
              />
            </svg>
            Table View
          </button>
          <button
            onClick={() => setViewMode("timeline")}
            className={`px-4 py-2 rounded-md text-sm font-medium flex items-center ${
              viewMode === "timeline"
                ? "bg-blue-600 text-white"
                : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v14"
              />
            </svg>
            Timeline View
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {transformedMembers.map((member, index) => (
          <div
            key={member.key}
            className="border rounded-lg shadow-sm overflow-hidden"
          >
            {/* Member Header with avatar */}
            <div className="flex items-center p-4 bg-gray-50 border-b">
              <div className="h-12 w-12 rounded-full flex items-center justify-center text-xl font-medium mr-3 shadow-sm border bg-gray-100 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700">
                {member.firstName && member.firstName.trim() !== ""
                  ? member.firstName.charAt(0).toUpperCase()
                  : member.lastName && member.lastName.trim() !== ""
                    ? member.lastName.charAt(0).toUpperCase()
                    : "H"}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {member.displayName}
                </h3>
              </div>
            </div>

            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">
                  Career & Education History
                </h4>
                <div className="space-x-2">
                  <button
                    onClick={() => handleEdit(member, "employment")}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Add Employment
                  </button>
                  <button
                    onClick={() => handleEdit(member, "education")}
                    className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    Add Education
                  </button>
                  {!member.military && (
                    <button
                      onClick={() => handleEdit(member, "military")}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      Add Military
                    </button>
                  )}
                </div>
              </div>

              {viewMode === "table" ? (
                renderTableView(member, index)
              ) : (
                <TimelineView
                  member={member}
                  onEdit={handleEdit}
                  index={index}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {selectedMember && modalType === "employment" && (
        <EmploymentModal
          isOpen={true}
          onClose={() => {
            setSelectedMember(null);
            setModalType(null);
          }}
          member={selectedMember}
          currentEmployment={selectedMember.employment}
          onSave={async (data) => {
            // Handle save
            setSelectedMember(null);
            setModalType(null);
          }}
        />
      )}

      {selectedMember && modalType === "education" && (
        <EducationModal
          isOpen={true}
          onClose={() => {
            setSelectedMember(null);
            setModalType(null);
          }}
          member={selectedMember}
          currentEducation={selectedMember.education}
          onSave={async (data) => {
            // Handle save
            setSelectedMember(null);
            setModalType(null);
          }}
        />
      )}

      {selectedMember && modalType === "military" && (
        <MilitaryModal
          isOpen={true}
          onClose={() => {
            setSelectedMember(null);
            setModalType(null);
          }}
          member={selectedMember}
          currentMilitary={selectedMember.military}
          onSave={async (data) => {
            // Handle save
            setSelectedMember(null);
            setModalType(null);
          }}
        />
      )}
    </div>
  );
};

export default CareerEducation;
